import {
  useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode,
} from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SearchIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { groupByCandidate } from '@/lib/candidates';
import { useContent, useLabel, useT, useTn } from '@/lib/i18n';
import { useSearchShortcut } from '@/lib/shortcuts';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Kbd } from '@/components/ui/kbd';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The topbar search (defect X2).
 *
 * ### What was wrong
 *
 * The input was `disabled` and its placeholder read *"Search questions,
 * candidates, modules…"*. A disabled control that describes a capability is a
 * promise; every operator who tried it learned that the console's most prominent
 * affordance does nothing.
 *
 * ### It searches exactly what the placeholder says, and nothing else
 *
 * This is the constraint the whole component is shaped by — **no search endpoint
 * was added**, so each group is built out of what the API already answers:
 *
 *   - **Questions** — `GET /api/questions?q=` is the one endpoint with real text
 *     search (a regex over `prompt.en` and `prompt.ar`). Server-side, debounced,
 *     five rows.
 *   - **Modules** — no text parameter exists, but the console already holds the
 *     whole catalogue under `['modules']` (`?pageSize=100`), so it is filtered
 *     here. No request is added to any screen that was not already making it.
 *   - **Candidates** — there is no candidates endpoint at all. The roster is
 *     derived from the instance feed by `lib/candidates.ts`, the same derivation
 *     the Candidates screen uses.
 *
 * Personas are deliberately **absent**: they would need the same client-side
 * filtering as modules, but the placeholder does not offer them and a group
 * nobody was promised is not worth a fourth query. Adding them means adding them
 * to the placeholder in both languages, not quietly widening the panel.
 *
 * ### Where a row goes
 *
 * A question has no route of its own — the item bank is a list with an editor
 * dialog — so a question row opens the bank filtered to that item's prompt,
 * which `?q=` resolves exactly because it is the same server-side regex. A
 * candidate row goes to the **dossier** (`/candidates/:ref`), never to `/api/…`:
 * that was defect X1, a plain navigation to the API that came back 401 as JSON.
 */

/** Below this, nothing is requested: one letter matches most of a bank. */
const MIN_QUERY = 2;
/**
 * 250ms. Long enough that a typed word is one request rather than five, short
 * enough that the panel still feels like it is following the keystrokes — and
 * the questions query is the only one that reaches the network at all.
 */
const DEBOUNCE_MS = 250;
/** Per group. The panel is a way in, not a second list screen. */
const PER_GROUP = 5;

const LISTBOX_ID = 'global-search-listbox';

/** One navigable row: a result, or the "see all" that ends its group. */
interface SearchOption {
  id: string;
  to: string;
  /** Rendered by the group that owns it; kept here so `Enter` needs no lookup. */
  render: () => ReactNode;
}

export function GlobalSearch() {
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const content = useContent();
  const navigate = useNavigate();

  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  // `fallback`: a list screen's own search box keeps `/` when there is one.
  useSearchShortcut(inputRef, { fallback: true });

  const trimmed = term.trim();

  useEffect(() => {
    if (trimmed.length < MIN_QUERY) {
      setDebounced('');
      return;
    }
    const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  useEffect(() => setActive(0), [debounced]);

  /**
   * Nothing is fetched below the minimum — including the two cached queries.
   * `['modules']` and `['assessments']` are shared keys, so on a screen that
   * already reads them this costs nothing, and on one that does not, an idle
   * search box must not make the request anyway.
   */
  const enabled = debounced.length >= MIN_QUERY;

  const questions = useQuery({
    queryKey: ['search', 'questions', debounced],
    queryFn: () => api.questions({ q: debounced, pageSize: PER_GROUP }),
    enabled,
  });
  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules, enabled });
  const assessments = useQuery({
    queryKey: ['assessments'],
    queryFn: () => api.assessments({ pageSize: 100 }),
    enabled,
  });

  const needle = debounced.toLowerCase();

  const moduleHits = useMemo(() => {
    if (!enabled) return [];
    return (modules.data?.items ?? [])
      .filter(
        (m) =>
          m.moduleId.toLowerCase().includes(needle)
          // Both halves of the bilingual name, whichever language the operator
          // reads in: an Arabic-only match still has to be findable.
          || m.name.en.toLowerCase().includes(needle)
          || m.name.ar.toLowerCase().includes(needle),
      )
      .slice(0, PER_GROUP);
  }, [enabled, modules.data, needle]);

  const candidateHits = useMemo(() => {
    if (!enabled) return [];
    return groupByCandidate(assessments.data?.items ?? [], [])
      // On the REFERENCE only. The Candidates screen also matches a module or
      // persona id, which is right for a filter over a candidate list and wrong
      // here — a candidate surfacing because their module matched would sit
      // directly under the module that matched, explaining nothing.
      .filter((row) => row.candidateRef.toLowerCase().includes(needle))
      .slice(0, PER_GROUP);
  }, [enabled, assessments.data, needle]);

  const questionHits = enabled ? (questions.data?.items ?? []) : [];

  const encoded = encodeURIComponent(debounced);

  /**
   * A module's name from the catalogue this component already holds.
   *
   * Not `useModuleName()`: that hook fetches `['modules']` unconditionally, and
   * this component lives in the layout — so it would put a modules request on
   * every screen in the console, including the ones that never mention one.
   * Unknown id renders as itself, exactly as the hook does.
   */
  const moduleName = (moduleId: string) => {
    const found = (modules.data?.items ?? []).find((m) => m.moduleId === moduleId);
    return found ? content(found.name) : moduleId;
  };

  /**
   * The flat, ordered list the keyboard walks — groups included, because a
   * "see all" an operator can see and cannot reach is the same defect in
   * miniature.
   */
  const options = useMemo<SearchOption[]>(() => {
    const list: SearchOption[] = [];

    for (const item of questionHits) {
      list.push({
        id: `search-question-${item.itemId}`,
        // No per-item route exists; `?q=` is the same regex the server just
        // matched, so the bank opens filtered to this prompt.
        to: `/questions?q=${encodeURIComponent(content(item.prompt))}`,
        render: () => (
          <>
            <span className="block truncate">{content(item.prompt)}</span>
            <span className="text-muted-foreground block truncate text-xs">
              {moduleName(item.moduleId)} · {label('status', item.status)}
            </span>
          </>
        ),
      });
    }
    if (questionHits.length > 0) {
      list.push({
        id: 'search-all-questions',
        to: `/questions?q=${encoded}`,
        render: () => t('search.seeAll.questions', { query: debounced }),
      });
    }

    for (const m of moduleHits) {
      list.push({
        id: `search-module-${m.moduleId}`,
        to: `/modules?q=${encodeURIComponent(m.moduleId)}`,
        render: () => (
          <>
            <span className="block truncate">{content(m.name)}</span>
            <span className="text-muted-foreground block truncate font-mono text-xs">
              {m.moduleId} · {label('moduleStatus', m.status)}
            </span>
          </>
        ),
      });
    }
    if (moduleHits.length > 0) {
      list.push({
        id: 'search-all-modules',
        to: `/modules?q=${encoded}`,
        render: () => t('search.seeAll.modules', { query: debounced }),
      });
    }

    for (const row of candidateHits) {
      list.push({
        id: `search-candidate-${row.candidateRef}`,
        // The W1 dossier. X1 was this link pointing at `/api/…`.
        to: `/candidates/${encodeURIComponent(row.candidateRef)}`,
        render: () => (
          <>
            <span className="block truncate font-mono" dir="ltr">
              {row.candidateRef}
            </span>
            <span className="text-muted-foreground block truncate text-xs">
              {tn('candidates.instances', row.instances.length)}
            </span>
          </>
        ),
      });
    }
    if (candidateHits.length > 0) {
      list.push({
        id: 'search-all-candidates',
        to: `/candidates?q=${encoded}`,
        render: () => t('search.seeAll.candidates', { query: debounced }),
      });
    }

    return list;
  }, [
    questionHits, moduleHits, candidateHits, modules.data,
    content, label, t, tn, debounced, encoded,
  ]);

  const open = trimmed.length >= MIN_QUERY && !dismissed;
  const activeIndex = options.length === 0 ? -1 : Math.min(active, options.length - 1);
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;

  /** Still waiting: either the debounce has not fired, or a query is on its way. */
  const busy =
    debounced !== trimmed
    || questions.isLoading
    || modules.isLoading
    || assessments.isLoading;

  const error = questions.error ?? modules.error ?? assessments.error;

  const go = (to: string) => {
    setDismissed(true);
    navigate(to);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setDismissed(true);
      return;
    }
    if (!open || options.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => (Math.min(i, options.length - 1) + 1) % options.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => (Math.min(i, options.length - 1) + options.length - 1) % options.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (activeOption) go(activeOption.to);
    }
  };

  /** Renders a group heading and its rows, given where its rows start. */
  const renderGroup = (key: string, titleKey: 'questions' | 'modules' | 'candidates', from: number, count: number) => {
    if (count === 0) return null;
    const headingId = `${LISTBOX_ID}-${key}`;
    return (
      <div role="group" aria-labelledby={headingId} className="py-1">
        <p
          id={headingId}
          className="text-muted-foreground px-3 py-1 text-[10.5px] font-bold tracking-[0.09em] uppercase"
        >
          {t(`search.group.${titleKey}`)}
        </p>
        {options.slice(from, from + count).map((option, offset) => {
          const index = from + offset;
          const isLast = offset === count - 1;
          return (
            <Link
              key={option.id}
              id={option.id}
              role="option"
              aria-selected={index === activeIndex}
              // Arrow keys are the cursor here; Tab should leave the search
              // rather than walk twenty rows.
              tabIndex={-1}
              to={option.to}
              onClick={() => setDismissed(true)}
              onMouseEnter={() => setActive(index)}
              className={cn(
                'block px-3 py-1.5 text-sm',
                index === activeIndex && 'bg-accent text-accent-foreground',
                isLast && 'text-primary',
              )}
            >
              {option.render()}
            </Link>
          );
        })}
      </div>
    );
  };

  // Group sizes include the "see all" row each group ends with.
  const questionBlock = questionHits.length > 0 ? questionHits.length + 1 : 0;
  const moduleBlock = moduleHits.length > 0 ? moduleHits.length + 1 : 0;
  const candidateBlock = candidateHits.length > 0 ? candidateHits.length + 1 : 0;

  return (
    <div
      className="relative hidden max-w-md flex-1 md:block"
      // Leaving the whole control closes it; moving between the input and a row
      // inside it does not.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDismissed(true);
      }}
    >
      <SearchIcon className="text-muted-foreground pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2" />
      <Input
        ref={inputRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={open && activeOption ? activeOption.id : undefined}
        autoComplete="off"
        className="ps-8 pe-9"
        placeholder={t('chrome.searchPlaceholder')}
        aria-label={t('common.search')}
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
          setDismissed(false);
        }}
        onFocus={() => setDismissed(false)}
        onKeyDown={onKeyDown}
      />
      {term === '' && (
        <Kbd className="absolute end-2.5 top-1/2 -translate-y-1/2">/</Kbd>
      )}

      {open && (
        /**
         * A plain anchored panel rather than a `Popover`: a popover moves focus
         * into its content and owns its own dismissal, and this listbox must
         * leave focus in the input for `aria-activedescendant` to mean anything.
         */
        <div className="bg-popover text-popover-foreground absolute top-full z-50 mt-1 w-full overflow-hidden rounded-md border shadow-md">
          {error ? (
            <div className="px-3 py-4 text-sm">
              <p className="font-medium">{t('search.error')}</p>
              <p className="text-muted-foreground text-xs">
                {error instanceof Error ? error.message : t('table.errorDescription')}
              </p>
            </div>
          ) : busy ? (
            <div className="space-y-2 p-3" aria-live="polite" aria-busy="true">
              <span className="sr-only">{t('search.searching')}</span>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : options.length === 0 ? (
            <div className="px-3 py-4 text-sm">
              <p className="font-medium">{t('search.empty', { query: debounced })}</p>
              <p className="text-muted-foreground text-xs text-balance">{t('search.scope')}</p>
            </div>
          ) : (
            <ScrollArea className="max-h-96">
              <div id={LISTBOX_ID} role="listbox" aria-label={t('search.results')}>
                {renderGroup('questions', 'questions', 0, questionBlock)}
                {renderGroup('modules', 'modules', questionBlock, moduleBlock)}
                {renderGroup(
                  'candidates', 'candidates', questionBlock + moduleBlock, candidateBlock,
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}
